import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  User, Mail, Lock, Phone, AlertCircle,
  Atom, Beaker, BookOpen, GraduationCap, 
  Microscope, Pencil, Ruler, Lightbulb, 
  Globe, Settings, Brain, Calculator,
  Dna, Orbit, Sigma, Box, Binary,
  Layers, Variable, Pi, CircleDashed, Cpu,
  Search, Star, Trophy, FlaskConical, Thermometer,
  Database, Code, Terminal, Infinity, Zap,
  Puzzle, MousePointer2, ClipboardList
} from 'lucide-react';
import logger from '../utils/logger';

const SignUp = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [mobile, setMobile] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  // Stable random icons for the background
  const backgroundIcons = React.useMemo(() => {
    const iconTypes = [
      Atom, Beaker, BookOpen, GraduationCap, Microscope, Pencil, Ruler, Lightbulb, 
      Globe, Settings, Brain, Calculator, Dna, Orbit, Sigma, Box, Binary, 
      Layers, Variable, Pi, CircleDashed, Cpu, Search, Star, Trophy, 
      FlaskConical, Thermometer, Database, Code, Terminal, Infinity, Zap,
      Puzzle, MousePointer2, ClipboardList
    ];
    
    const rows = 16;
    const cols = 16;
    const items = [];
    
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        const index = r * cols + c;
        items.push({
          Icon: iconTypes[index % iconTypes.length],
          top: `${(r / rows) * 100 + (Math.random() * 4 - 2)}%`,
          left: `${(c / cols) * 100 + (Math.random() * 4 - 2)}%`,
          rotate: `${(Math.random() - 0.5) * 40}deg`,
          scale: 0.8 + Math.random() * 0.5,
          opacity: 0.1 + Math.random() * 0.15
        });
      }
    }
    return items;
  }, []);

  // Password strength checker
  const getPasswordStrength = (pwd) => {
    const checks = {
      length: pwd.length >= 8,
      lowercase: /[a-z]/.test(pwd),
      uppercase: /[A-Z]/.test(pwd),
      number: /\d/.test(pwd),
      special: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(pwd)
    };
    
    const passedChecks = Object.values(checks).filter(Boolean).length;
    
    let strength = 'weak';
    if (passedChecks === 5) strength = 'strong';
    else if (passedChecks >= 3) strength = 'medium';
    
    return { strength, passedChecks, totalChecks: 5, checks };
  };

  const passwordStrength = getPasswordStrength(password);
  
  const strengthColors = {
    weak: 'bg-red-500',
    medium: 'bg-yellow-500',
    strong: 'bg-green-500'
  };
  
  const strengthLabels = {
    weak: 'Weak',
    medium: 'Medium',
    strong: 'Strong'
  };

  const validateAustralianMobile = (mobile) => {
    const cleanMobile = mobile.replace(/\s|\(|\)|-/g, '');
    const australianMobileRegex = /^(?:\+61|0)4\d{8}$/;
    return australianMobileRegex.test(cleanMobile);
  };

  const formatMobileDisplay = (mobile) => {
    const cleanMobile = mobile.replace(/\s|\(|\)|\+/g, '');
    if (cleanMobile.startsWith('61') && cleanMobile.length === 11) {
      const formatted = '0' + cleanMobile.substring(2);
      return formatted.replace(/(\d{4})(\d{3})(\d{3})/, '$1 $2 $3');
    } else if (cleanMobile.startsWith('0') && cleanMobile.length === 10) {
      return cleanMobile.replace(/(\d{4})(\d{3})(\d{3})/, '$1 $2 $3');
    }
    return mobile;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (passwordStrength.strength !== 'strong') {
      setError('Password must include lowercase, uppercase, number, special character, and be at least 8 characters');
      return;
    }

    if (!validateAustralianMobile(mobile)) {
      setError('Please enter a valid Australian mobile number');
      return;
    }

    setLoading(true);

    try {
      await register(email, password, name, mobile);
      navigate('/confirm-signup', { state: { email } });
    } catch (err) {
      setError(err.message || 'Failed to create account. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#0d47a1] via-[#1565c0] to-[#0d47a1] relative overflow-hidden py-12 px-4 sm:px-6 lg:px-8">
      {/* Randomized Educational Icon Pattern Background */}
      <div className="absolute inset-0 pointer-events-none select-none">
        {backgroundIcons.map((item, i) => (
          <div 
            key={i} 
            className={`absolute pointer-events-auto cursor-default group 
              ${i % 3 === 0 ? 'hidden sm:block' : ''} 
              ${i % 5 === 0 ? 'hidden md:block' : ''} 
              ${i % 7 === 0 ? 'hidden lg:block' : ''}`}
            style={{ 
              top: item.top,
              left: item.left,
              transform: `rotate(${item.rotate}) scale(${item.scale})`,
              opacity: item.opacity,
              zIndex: 0
            }}
          >
            <div className="transition-all duration-500 ease-out group-hover:scale-[1.5] group-hover:rotate-[15deg] group-hover:opacity-100 text-white">
              <item.Icon className="w-5 h-5 sm:w-10 sm:h-10 md:w-12 md:h-12 drop-shadow-[0_0_8px_rgba(255,255,255,0.2)] group-hover:drop-shadow-[0_0_15px_rgba(255,255,255,0.5)]" strokeWidth={1.5} />
            </div>
          </div>
        ))}
      </div>

      {/* Radiant Glows */}
      <div className="absolute top-[-10%] right-[-5%] w-[600px] h-[600px] bg-blue-400/20 rounded-full blur-[120px] pointer-events-none" />
      <div className="absolute bottom-[-10%] left-[-5%] w-[600px] h-[600px] bg-indigo-400/20 rounded-full blur-[120px] pointer-events-none" />

      <div className="max-w-md w-full space-y-8 relative z-10">
        <div className="text-center">
          <div className="flex justify-center items-center space-x-3 mb-8">
            <img 
              src="/logo/nep-logo.png" 
              alt="NEP" 
              className="h-16 w-auto drop-shadow-[0_0_20px_rgba(255,255,255,0.4)]"
            />
            <div className="flex flex-col text-left border-l-2 border-white/20 pl-4">
              <span className="text-4xl font-bold text-white tracking-tight">NEP</span>
              <span className="text-xs font-semibold text-blue-200 tracking-wider">NextGen Exam Platform</span>
            </div>
          </div>
        </div>

        {/* Glass Card */}
        <div className="bg-white/[0.08] backdrop-blur-3xl border border-white/20 rounded-3xl p-10 shadow-[0_20px_50px_rgba(0,0,0,0.3)]">
          <form className="space-y-6" onSubmit={handleSubmit}>
            <div className="text-center mb-10">
              <h2 className="text-3xl font-bold text-white">Create Account</h2>
              <p className="text-blue-100 text-sm mt-2 font-medium">Join our community of learners</p>
            </div>
            
            {error && (
              <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-4 flex items-center space-x-2">
                <AlertCircle className="h-5 w-5 text-red-400" />
                <span className="text-sm text-red-200">{error}</span>
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-300 mb-1.5 ml-1">
                  Full Name
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <User className="h-5 w-5 text-blue-400/70" />
                  </div>
                  <input
                    id="name"
                    name="name"
                    type="text"
                    required
                    className="w-full pl-11 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/60 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all outline-none"
                    placeholder="Enter your Full Name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                  />
                </div>
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-1.5 ml-1">
                  Email address
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <Mail className="h-5 w-5 text-blue-400/70" />
                  </div>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    className="w-full pl-11 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/60 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all outline-none"
                    placeholder="Email ID"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </div>
              </div>

              <div>
                <label htmlFor="mobile" className="block text-sm font-medium text-gray-300 mb-1.5 ml-1">
                  Mobile Number
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <Phone className="h-5 w-5 text-blue-400/70" />
                  </div>
                  <input
                    id="mobile"
                    name="mobile"
                    type="tel"
                    required
                    className="w-full pl-11 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/60 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all outline-none"
                    placeholder="04xx xxx xxx"
                    value={mobile}
                    onChange={(e) => setMobile(formatMobileDisplay(e.target.value))}
                    maxLength="12"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-300 mb-1.5 ml-1">
                  Password
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <Lock className="h-5 w-5 text-blue-400/70" />
                  </div>
                  <input
                    id="password"
                    name="password"
                    type="password"
                    required
                    className="w-full pl-11 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/60 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all outline-none"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                </div>
                {/* Password Strength Meter */}
                {password && (
                  <div className="mt-3 space-y-2">
                    <div className="flex items-center space-x-2">
                      <div className="flex-1 h-1.5 bg-white/10 rounded-full overflow-hidden">
                        <div 
                          className={`h-full transition-all duration-300 ${strengthColors[passwordStrength.strength]}`}
                          style={{ width: `${(passwordStrength.passedChecks / passwordStrength.totalChecks) * 100}%` }}
                        />
                      </div>
                      <span className={`text-[10px] font-bold uppercase tracking-wider ${
                        passwordStrength.strength === 'weak' ? 'text-red-400' :
                        passwordStrength.strength === 'medium' ? 'text-yellow-400' :
                        'text-green-400'
                      }`}>
                        {strengthLabels[passwordStrength.strength]}
                      </span>
                    </div>
                  </div>
                )}
              </div>

              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-300 mb-1.5 ml-1">
                  Confirm Password
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <Lock className="h-5 w-5 text-blue-400/70" />
                  </div>
                  <input
                    id="confirmPassword"
                    name="confirmPassword"
                    type="password"
                    required
                    className="w-full pl-11 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/60 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all outline-none"
                    placeholder="••••••••"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                  />
                </div>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3.5 px-4 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-500 hover:to-blue-600 text-white rounded-xl font-semibold shadow-lg shadow-blue-900/20 transition-all transform hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed flex justify-center items-center space-x-2"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-white/30 border-t-white"></div>
                  <span>Creating Account...</span>
                </>
              ) : (
                <span>Sign up</span>
              )}
            </button>

            <div className="text-center space-y-4 pt-2">
              <p className="text-sm text-gray-400">
                Already have an account?{' '}
                <Link to="/signin" className="font-semibold text-blue-400 hover:text-blue-300 transition-colors">
                  Sign in
                </Link>
              </p>
              <Link to="/" className="inline-block text-sm font-medium text-white/60 hover:text-white transition-colors">
                ← Back to Home
              </Link>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default SignUp;
